import React, { useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
import { GiftIcon, FileTextIcon, WrenchIcon, ShieldIcon } from 'lucide-react';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.2
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
export function BonusSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="w-full bg-dark-section py-10 md:py-14" data-id="element-20">
      <div className="max-w-4xl mx-auto px-6" data-id="element-21">
        <motion.div className="text-center mb-12" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.6
      }} data-id="element-22">
          <span className="inline-block px-3 py-1 text-xs font-inter font-bold tracking-[0.2em] text-orange-accent uppercase border border-orange-accent/50 bg-orange-accent/10 mb-4" data-id="element-23">
            ORDER TODAY AND GET
          </span>
          <h2 className="font-bebas text-4xl md:text-5xl lg:text-6xl text-white tracking-wide" data-id="element-24">
            3 FREE <span className="text-orange-accent" data-id="element-25">BONUSES</span>
          </h2>
        </motion.div>

        <motion.div className="space-y-6" variants={containerVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-26">
          {/* Bonus 1 */}
          <motion.div variants={cardVariants} className="bg-dark-card border border-orange-accent/20 p-6 md:p-8 flex gap-6" data-id="element-27">
            <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center bg-orange-accent/10 border border-orange-accent/30" data-id="element-28">
              <FileTextIcon className="w-7 h-7 text-orange-accent" data-id="element-29" />
            </div>
            <div className="flex-1" data-id="element-30">
              <div className="flex items-center gap-3 mb-2" data-id="element-31">
                <span className="font-bebas text-orange-accent text-lg" data-id="element-32">
                  BONUS #1
                </span>
                <span className="font-inter text-concrete/60 text-sm line-through" data-id="element-33">
                  Value: $47
                </span>
              </div>
              <h3 className="font-bebas text-2xl text-white mb-2 tracking-wide" data-id="element-34">
                THE "GRAY MAN" PROCUREMENT GUIDE
              </h3>
              <p className="font-inter text-concrete leading-relaxed" data-id="element-35">
                How to buy all your materials without creating a digital
                footprint. Cash purchasing strategies, store rotation schedules,
                and how to avoid loyalty cards and cameras that track your
                purchases.
              </p>
            </div>
          </motion.div>

          {/* Bonus 2 */}
          <motion.div variants={cardVariants} className="bg-dark-card border border-orange-accent/20 p-6 md:p-8 flex gap-6" data-id="element-36">
            <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center bg-orange-accent/10 border border-orange-accent/30" data-id="element-37">
              <WrenchIcon className="w-7 h-7 text-orange-accent" data-id="element-38" />
            </div>
            <div className="flex-1" data-id="element-39">
              <div className="flex items-center gap-3 mb-2" data-id="element-40">
                <span className="font-bebas text-orange-accent text-lg" data-id="element-41">
                  BONUS #2
                </span>
                <span className="font-inter text-concrete/60 text-sm line-through" data-id="element-42">
                  Value: $37
                </span>
              </div>
              <h3 className="font-bebas text-2xl text-white mb-2 tracking-wide" data-id="element-43">
                TOOL-FREE INSTALLATION BLUEPRINTS
              </h3>
              <p className="font-inter text-concrete leading-relaxed" data-id="element-44">
                Alternative build methods that require zero power tools. Perfect
                for apartment dwellers or those who want to avoid the noise of
                construction. Includes hand-tool-only techniques used by
                old-school contractors.
              </p>
            </div>
          </motion.div>

          {/* Bonus 3 */}
          <motion.div variants={cardVariants} className="bg-dark-card border border-orange-accent/20 p-6 md:p-8 flex gap-6" data-id="element-45">
            <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center bg-orange-accent/10 border border-orange-accent/30" data-id="element-46">
              <ShieldIcon className="w-7 h-7 text-orange-accent" data-id="element-47" />
            </div>
            <div className="flex-1" data-id="element-48">
              <div className="flex items-center gap-3 mb-2" data-id="element-49">
                <span className="font-bebas text-orange-accent text-lg" data-id="element-50">
                  BONUS #3
                </span>
                <span className="font-inter text-concrete/60 text-sm line-through" data-id="element-51">
                  Value: $29
                </span>
              </div>
              <h3 className="font-bebas text-2xl text-white mb-2 tracking-wide" data-id="element-52">
                DECOY SAFE STRATEGY MANUAL
              </h3>
              <p className="font-inter text-concrete leading-relaxed" data-id="element-53">
                How to set up a cheap "sacrificial" safe with convincing decoy
                items. Give thieves something to find — and feel like they won —
                while your real collection stays invisible. Includes
                psychological misdirection tactics.
              </p>
            </div>
          </motion.div>
        </motion.div>

        <motion.p className="text-center mt-10 font-inter text-lg text-white" initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {
        opacity: 0
      }} transition={{
        duration: 0.6,
        delay: 0.6
      }} data-id="element-54">
          Total Bonus Value:{' '}
          <span className="line-through text-concrete" data-id="element-55">$113</span>{' '}
          <span className="text-orange-accent font-bold" data-id="element-56">FREE</span> when you
          order today
        </motion.p>
      </div>
    </section>;
}